import { Fragment, useEffect, useState } from 'react';
import { friendlyError } from '@generated/hooks/friendlyError';
import { getStaffPass } from '@generated/hooks/staffPass';
import { Loader2, Paperclip, ChevronDown, Trash2 } from 'lucide-react';
import { getOrders, getStatusCounts, deleteOrders } from '@generated/server/storefront';
import { copyOrderToAdList } from '@generated/server/adlist';
import { sendContract } from '@generated/server/contract';
import { money } from '@generated/components/pricing';
import { orderInsight } from '@generated/components/orderInsights';

type Order = Awaited<ReturnType<typeof getOrders>>['items'][number];
type Count = Awaited<ReturnType<typeof getStatusCounts>>[number];
const PUB_FILTERS = ['', 'Red Hook', 'Village', 'Bundle'];

export function Pipeline() {
  const [pub, setPub] = useState('');
  const [status, setStatus] = useState('');
  const [items, setItems] = useState<Order[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [counts, setCounts] = useState<Count[]>([]);
  const [loading, setLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [copying, setCopying] = useState<string | null>(null);
  const [copyMsg, setCopyMsg] = useState<{ id: string; text: string; ok: boolean } | null>(null);
  const copyToList = async (o: Order) => {
    setCopying(o.id); setCopyMsg(null);
    const prev = items;
    try {
      const r = await copyOrderToAdList({ data: { pass: getStaffPass(), orderId: o.id } });
      setItems((p) => p.map((x) => (x.id === o.id ? { ...x, status: 'Copied to Monthly List' } : x)));
      setCopyMsg({ id: o.id, ok: true, text: `Added ${r.added} ad(s) to the Ad List: ${r.months.join(', ')}.` });
    } catch (e) { console.error(e); setItems(prev); setCopyMsg({ id: o.id, ok: false, text: e instanceof Error && e.message ? e.message : 'Could not copy this order.' }); }
    finally { setCopying(null); }
  };
  const [sending, setSending] = useState<string | null>(null);
  const [sendMsg, setSendMsg] = useState<{ id: string; text: string; ok: boolean } | null>(null);
  const resendContract = async (o: Order) => {
    setSending(o.id); setSendMsg(null);
    try {
      const r = await sendContract({ data: { orderId: o.id, pass: getStaffPass(), staff: true } });
      setSendMsg({ id: o.id, ok: !!r?.customer, text: r?.customer ? `Contract emailed to ${o.email?.email ?? 'the advertiser'}${r.george ? ' and you' : ''}.` : 'Could not email the advertiser. Check their email address and try again.' });
    } catch (e) { console.error(e); setSendMsg({ id: o.id, ok: false, text: 'Could not send the contract.' }); }
    finally { setSending(null); }
  };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const toggleSel = (id: string) => setSelected((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allSel = items.length > 0 && items.every((o) => selected.has(o.id));
  const toggleAll = () => setSelected(allSel ? new Set() : new Set(items.map((o) => o.id)));
  const removeSelected = async () => {
    const ids = [...selected];
    const prev = items;
    setDeleting(true); setConfirmDel(false); setError(null);
    setItems((p) => p.filter((o) => !selected.has(o.id)));
    try {
      const r = await deleteOrders({ data: { pass: getStaffPass(), ids } });
      const failed = r?.failed ?? [];
      if (failed.length) { setItems(prev.filter((o) => !ids.includes(o.id) || failed.includes(o.id))); setError(r?.denied ? `monday.com didn't allow the portal to delete ${failed.length === 1 ? 'this order' : 'these orders'}. Delete ${failed.length === 1 ? 'it' : 'them'} on the Campaign Intake board, or allow board editing (see instructions).` : `${failed.length} order(s) could not be deleted.`); }
      setSelected(new Set(failed));
      getStatusCounts({ data: { pass: getStaffPass() } }).then((c) => setCounts(c ?? [])).catch((e) => console.error(e));
    } catch (e) { console.error(e); setItems(prev); setError('Could not delete the selected orders. Please try again.'); }
    finally { setDeleting(false); }
  };

  useEffect(() => { getStatusCounts({ data: { pass: getStaffPass() } }).then((r) => setCounts(r ?? [])).catch((e) => console.error(e)); }, []);

  useEffect(() => {
    let active = true;
    setRefetching(true); setError(null);
    getOrders({ data: { pass: getStaffPass(), publication: pub || undefined, status: status || undefined } })
      .then((r) => { if (active) { setItems(r?.items ?? []); setCursor(r?.cursor ?? null); } })
      .catch((e) => { console.error(e); if (active) setError(friendlyError(e, 'Could not load orders.')); })
      .finally(() => { if (active) { setLoading(false); setRefetching(false); } });
    return () => { active = false; };
  }, [pub, status]);

  const more = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try { const r = await getOrders({ data: { pass: getStaffPass(), cursor } }); setItems((p) => [...p, ...(r?.items ?? [])]); setCursor(r?.cursor ?? null); }
    catch (e) { console.error(e); setError('Could not load more orders.'); }
    finally { setLoadingMore(false); }
  };

  const statuses = counts.map((c) => c.status).filter(Boolean);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-px border border-foreground bg-foreground sm:grid-cols-4 lg:grid-cols-7">
        {counts.map((c) => (
          <button key={c.status || 'none'} onClick={() => setStatus(status === c.status ? '' : c.status ?? '')}
            className={`p-3 text-left ${status === c.status ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`}>
            <div className="text-[10px] uppercase tracking-wider opacity-70">{c.status || 'No status'}</div>
            <div className="font-[family-name:var(--font-heading)] text-2xl">{c.total}</div>
            <div className="text-xs opacity-70">{money(c.value ?? 0)}</div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {PUB_FILTERS.map((p) => (
          <button key={p || 'all'} onClick={() => setPub(p)} className={`min-h-9 border px-3 text-xs font-semibold uppercase tracking-wider ${pub === p ? 'border-foreground bg-foreground text-background' : 'border-border bg-card'}`}>{p || 'All papers'}</button>
        ))}
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="min-h-9 border border-border bg-card px-2 text-sm" aria-label="Filter by status">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {refetching && !loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 border border-destructive bg-card px-3 py-2 text-sm">
          <span className="font-semibold">{selected.size} selected</span>
          {!confirmDel ? (
            <button onClick={() => setConfirmDel(true)} disabled={deleting} className="inline-flex min-h-9 items-center gap-1.5 bg-destructive px-3 text-xs font-semibold text-primary-foreground disabled:opacity-60">
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}Delete selected
            </button>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2">Delete {selected.size} order(s)?
              <button onClick={() => void removeSelected()} className="min-h-9 bg-destructive px-3 text-xs font-semibold text-primary-foreground">Yes, delete</button>
              <button onClick={() => setConfirmDel(false)} className="min-h-9 border border-border px-3 text-xs">Cancel</button>
            </span>
          )}
          <button onClick={() => { setSelected(new Set()); setConfirmDel(false); }} className="ml-auto text-xs underline underline-offset-2">Clear selection</button>
        </div>
      )}
      <div className={`overflow-x-auto border border-border bg-card ${refetching && !loading ? 'opacity-60' : ''}`}>
        <table className="w-full min-w-[760px] text-sm">
          <thead className="border-b-2 border-foreground text-left text-[11px] uppercase tracking-wider">
            <tr><th className="w-10 px-3 py-2"><input type="checkbox" aria-label="Select all orders" checked={allSel} onChange={toggleAll} className="h-4 w-4 accent-[hsl(var(--primary))]" /></th>{['Advertiser', 'Publication', 'Size', 'Term', 'Start', 'Value', 'Status', 'Files'].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && Array.from({ length: 5 }).map((_, i) => <tr key={i}><td colSpan={9} className="p-3"><div className="h-4 animate-pulse bg-muted" /></td></tr>)}
            {!loading && items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No orders match these filters.</td></tr>}
            {items.map((o) => (
              <Fragment key={o.id}>
              <tr onClick={() => setOpen(open === o.id ? null : o.id)} className={`cursor-pointer hover:bg-muted/50 ${selected.has(o.id) ? "bg-muted/60" : ""}`}>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Select order from ${o.name}`} checked={selected.has(o.id)} onChange={() => toggleSel(o.id)} className="h-4 w-4 accent-[hsl(var(--primary))]" /></td>
                <td className="px-3 py-2"><div className="font-medium">{o.name}</div><div className="text-xs text-muted-foreground">{o.contactPerson}</div>{(() => { const ins = orderInsight(o.specialInstructions, o.startMonth, o.duration); return <div className="mt-1 flex flex-wrap gap-1 text-[10px] uppercase tracking-wider">{ins.kind && <span className={`px-1.5 py-0.5 text-primary-foreground ${ins.kind === 'event' ? 'bg-primary' : 'bg-[hsl(var(--brand-blue))]'}`}>{ins.kind === 'event' ? 'Event' : 'Campaign'}</span>}{ins.followUp && <span className="border border-border px-1.5 py-0.5">Follow up {ins.followUp}</span>}</div>; })()}</td>
                <td className="px-3 py-2">{o.publication ?? '—'}</td>
                <td className="px-3 py-2">{o.adSize ?? '—'}</td>
                <td className="px-3 py-2">{o.duration ?? '—'}</td>
                <td className="px-3 py-2">{o.startMonth ?? '—'}</td>
                <td className="px-3 py-2 tabular-nums">{money(o.totalContractValue ?? 0)}</td>
                <td className="px-3 py-2"><span className="border border-foreground/30 px-2 py-0.5 text-xs">{o.status ?? '—'}</span></td>
                <td className="px-3 py-2"><span className="inline-flex items-center gap-1 text-xs"><Paperclip className="h-3.5 w-3.5" />{o.adFiles?.length ?? 0}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === o.id ? "rotate-180" : ""}`} /></span></td>
              </tr>
              {open === o.id && (
                <tr className="bg-muted/40"><td colSpan={9} className="px-3 py-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-1 text-sm"><div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contact</div><div>{o.contactPerson ?? "—"}</div><div>{o.email?.email ?? "—"}</div><div>{o.phone?.phone ?? "—"}</div><div className="text-muted-foreground">Pays by {o.paymentMethod ?? "—"}</div></div>
                    <div className="space-y-1 text-sm"><div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Ad files</div>
                      {(o.adFiles ?? []).length === 0 && <div className="text-muted-foreground">No files uploaded</div>}
                      {(o.adFiles ?? []).map((f) => <a key={f.id} href={f.url ?? "#"} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary underline underline-offset-2"><Paperclip className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{f.name}</span></a>)}
                    </div>
                    {(() => { const ins = orderInsight(o.specialInstructions, o.startMonth, o.duration); return (ins.facts.length > 0 || ins.followUp) ? (
                      <div className="space-y-1 text-sm md:col-span-3"><div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">About this advertiser</div>
                        <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">{ins.facts.map(([k, v]) => <div key={k} className="flex gap-2"><dt className="shrink-0 text-muted-foreground">{k}:</dt><dd className="min-w-0">{v}</dd></div>)}</dl>
                        {ins.followUp && <p className="pt-1"><strong>Follow up in {ins.followUp}.</strong> <span className="text-muted-foreground">{ins.followWhy}</span></p>}
                      </div>) : null; })()}
                    <div className="space-y-1 text-sm"><div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</div><p className="whitespace-pre-line">{o.specialInstructions || "—"}</p></div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3">
                    <button onClick={() => void copyToList(o)} disabled={copying === o.id || o.status === 'Copied to Monthly List'} className="inline-flex min-h-11 items-center gap-2 bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                      {copying === o.id && <Loader2 className="h-4 w-4 animate-spin" />}{o.status === 'Copied to Monthly List' ? 'Already on the Ad List' : 'Copy to Ad List'}
                    </button>
                    <button onClick={() => void resendContract(o)} disabled={sending === o.id} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50">
                      {sending === o.id && <Loader2 className="h-4 w-4 animate-spin" />}Send contract
                    </button>
                    {sendMsg?.id === o.id && <span className={`text-sm ${sendMsg.ok ? '' : 'text-destructive'}`}>{sendMsg.text}</span>}
                    {copyMsg?.id === o.id && <span className={`text-sm ${copyMsg.ok ? '' : 'text-destructive'}`}>{copyMsg.text}</span>}
                  </div>
                </td></tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {cursor && (
        <button onClick={more} disabled={loadingMore} className="mx-auto flex min-h-11 items-center gap-2 border border-foreground px-5 text-sm font-semibold disabled:opacity-60">
          {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}Load more orders
        </button>
      )}
    </div>
  );
}
